import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import { FaCalendarAlt } from 'react-icons/fa';
import { getRequestsByEmployee } from '../../wrms/services/wrmsService';
import { useAuth } from '../../context/AuthContext';

const formatDate = (ts) => {
  if (!ts) return '—';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return String(ts);
  }
};

const STATUS_CONFIG = {
  approved:  { bg: '#ecfdf5', color: '#065f46', label: 'Approved' },
  pending:   { bg: '#fffbeb', color: '#92400e', label: 'Pending'  },
  rejected:  { bg: '#fef2f2', color: '#991b1b', label: 'Rejected' },
  cancelled: { bg: '#f8fafc', color: '#475569', label: 'Cancelled' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status?.toLowerCase()] || { bg: '#f1f5f9', color: '#334155', label: status };
  return (
    <span style={{
      padding: '3px 10px', borderRadius: '20px', fontSize: '0.72rem',
      fontWeight: 700, background: cfg.bg, color: cfg.color
    }}>
      {cfg.label}
    </span>
  );
};

const LeaveBalanceSection = ({ employeeId }) => {
  const { user } = useAuth();
  const [leaves, setLeaves]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  useEffect(() => {
    const fetchLeaves = async () => {
      setLoading(true);
      setError(null);
      try {
        // WRMS stores employeeId as Firebase Auth UID (user.uid)
        // Try current user's UID first, fall back to the passed employeeId
        const uidToQuery = user?.uid || user?.id || employeeId;

        if (!uidToQuery) {
          setLeaves([]);
          setLoading(false);
          return;
        }

        console.log('[LeaveBalanceSection] Fetching WRMS leave requests for uid:', uidToQuery);

        // Fetch only leave-type requests for this employee
        let results = await getRequestsByEmployee(uidToQuery, 'leave');

        // If nothing found by UID, try with the Firestore employee doc ID
        if (results.length === 0 && employeeId && employeeId !== uidToQuery) {
          console.log('[LeaveBalanceSection] Retrying with employeeId:', employeeId);
          results = await getRequestsByEmployee(employeeId, 'leave');
        }

        // Sort newest first
        results.sort((a, b) => {
          const ta = a.submittedAt?.toMillis?.() || new Date(a.submittedAt || 0).getTime();
          const tb = b.submittedAt?.toMillis?.() || new Date(b.submittedAt || 0).getTime();
          return tb - ta;
        });

        console.log('[LeaveBalanceSection] Found', results.length, 'leave requests');
        setLeaves(results);
      } catch (err) {
        console.error('[LeaveBalanceSection] Error fetching leaves:', err);
        setError('Failed to load leave history. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchLeaves();
  }, [employeeId, user?.uid]);

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" variant="primary" size="sm" />
        <p className="mt-2 text-muted small">Loading leave history...</p>
      </div>
    );
  }

  if (error) return <Alert variant="danger" className="m-3">{error}</Alert>;

  return (
    <div>
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white border-bottom d-flex align-items-center justify-content-between py-3">
          <h6 className="mb-0 fw-bold">Recent Leave History</h6>
          <Badge bg="secondary" pill>{leaves.length} record{leaves.length !== 1 ? 's' : ''}</Badge>
        </Card.Header>
        <Card.Body className="p-0">
          {leaves.length === 0 ? (
            <div className="text-center text-muted py-5">
              <div style={{ marginBottom: '8px' }}>
                <FaCalendarAlt size={32} color="#94a3b8" />
              </div>
              <p className="mb-0">No leave history found.</p>
              <small>Your submitted leave requests will appear here.</small>
            </div>
          ) : (
            <Table hover responsive className="mb-0 align-middle">
              <thead className="bg-light">
                <tr style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 700 }}>
                  <th>Request ID</th>
                  <th>Leave Type</th>
                  <th>From</th>
                  <th>To</th>
                  <th className="text-center">Days</th>
                  <th className="text-center">Status</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {leaves.map((leave) => (
                  <tr key={leave.id} style={{ fontSize: '0.82rem' }}>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#4f46e5', fontWeight: 700 }}>
                      {leave.requestId || leave.id?.slice(0, 8)}
                    </td>
                    <td style={{ textTransform: 'capitalize' }}>
                      {(leave.leaveType || '').replace(/_/g, ' ')}
                    </td>
                    <td>{formatDate(leave.fromDate || leave.startDate)}</td>
                    <td>{formatDate(leave.toDate || leave.endDate)}</td>
                    <td className="text-center fw-bold">{leave.daysCount || leave.days || '—'}</td>
                    <td className="text-center">
                      <StatusBadge status={leave.status} />
                    </td>
                    <td style={{ color: '#64748b' }}>{formatDate(leave.submittedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default LeaveBalanceSection;
